import React, { useState, useEffect, useMemo } from 'react';
import api from './api';

// --- HELPER FUNCTIONS ---
const formatRound = (num) => Math.round(num || 0).toLocaleString('en-IN');
const formatDecimal = (num) => (parseFloat(num) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);

const calculateDueDate = (date, terms) => {
    const d = new Date(date);
    d.setDate(d.getDate() + parseInt(terms || 0));
    return d;
};

const getDueStatus = (date, terms) => {
    if (!terms) return { date: '-', days: '-', status: 'Normal' };
    const dueDate = calculateDueDate(date, terms);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    let status = 'Normal';
    if (diffDays < 0) status = 'Overdue';
    else if (diffDays <= 3) status = 'Urgent';
    
    return { date: dueDate.toLocaleDateString('en-GB'), days: diffDays, status };
};

const AccountsPayable = () => {
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [showModal, setShowModal] = useState(false);
    const [selectedBill, setSelectedBill] = useState(null);
    const [payForm, setPayForm] = useState({ amount: '', mode: 'Cheque', ref: '' });

    const fetchBills = async () => {
        setLoading(true);
        try {
            const res = await api.get('/rough');
            const payable = res.data.data.filter(b => b.paymentStatus !== 'Paid' && b.partyPayable > 0);
            setBills(payable);
        } catch (err) { alert("Error loading accounts"); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchBills(); }, []);

    const stats = useMemo(() => {
        let total = 0, billDue = 0, cashDue = 0, usdDue = 0;
        const months = {};

        bills.forEach(b => {
            const pending = (b.partyPayable || 0) - (b.paidAmount || 0);
            total += pending;
            if (b.billType === 'Cash') cashDue += pending;
            else billDue += pending;
            if (b.currency === 'USD') usdDue += (pending / (b.exchangeRate || 1));

            if (b.paymentTermsDays) {
                const due = calculateDueDate(b.purchaseDate, b.paymentTermsDays);
                const key = due.toLocaleString('default', { month: 'short', year: '2-digit' });
                if (!months[key]) months[key] = 0;
                months[key] += pending;
            }
        });
        return { total, billDue, cashDue, usdDue, months };
    }, [bills]);

    const openPaymentModal = (bill) => {
        const pending = (bill.partyPayable || 0) - (bill.paidAmount || 0);
        setSelectedBill(bill);
        setPayForm({ amount: pending, mode: 'Cheque', ref: '' });
        setShowModal(true);
    };

    const submitPayment = async (e) => {
        e.preventDefault();
        try {
            await api.post('/payments', {
                billId: selectedBill._id,
                party: selectedBill.party._id,
                amountPaid: payForm.amount,
                paymentMode: payForm.mode,
                referenceNumber: payForm.ref
            });
            alert("✅ Payment Recorded!");
            setShowModal(false);
            fetchBills(); 
        } catch (err) {
            alert("Error: " + (err.response?.data?.error || err.message));
        }
    };

    if (loading) return <div className="p-5 text-center">Loading Accounts...</div>;

    return (
        <div className="container mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold">Accounts Payable</h4>
                <button className="btn btn-sm btn-outline-dark" onClick={fetchBills}>↻ Refresh</button>
            </div>

            {/* DASHBOARD */}
            <div className="row mb-4 g-3">
                <div className="col-12 col-md-3">
                    <div className="card bg-primary text-white h-100 shadow-sm">
                        <div className="card-body text-center">
                            <small className="opacity-75">Total Outstanding</small>
                            <h3 className="fw-bold mb-0">₹ {formatRound(stats.total)}</h3>
                            {stats.usdDue > 0 && <small className="d-block mt-1 text-warning">($ {formatUSD(stats.usdDue)})</small>}
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-body p-2 p-md-3">
                            <div className="d-flex justify-content-between mb-2 border-bottom pb-1">
                                <span className="text-muted small">Bill</span>
                                <span className="fw-bold small">₹ {formatRound(stats.billDue)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted small">Cash</span>
                                <span className="fw-bold text-success small">₹ {formatRound(stats.cashDue)}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className="card bg-light h-100 border-0">
                        <div className="card-body py-2">
                            <small className="fw-bold text-muted">Upcoming (Monthly)</small>
                            <div className="d-flex gap-2 overflow-auto mt-2" style={{whiteSpace:'nowrap'}}>
                                {Object.entries(stats.months).sort().map(([month, amount]) => (
                                    <div key={month} className="bg-white border px-3 py-1 rounded text-center">
                                        <div className="small fw-bold">{month}</div>
                                        <div className="text-primary small">₹ {(amount/100000).toFixed(1)}L</div>
                                    </div>
                                ))}
                                {Object.keys(stats.months).length === 0 && <span className="text-muted small">No upcoming dues.</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* DESKTOP TABLE */}
            <div className="card shadow-sm border-0 d-none d-md-block">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>RP No</th>
                                <th>Party</th>
                                <th>Type</th>
                                <th className="text-end">Weight</th>
                                <th className="text-end">Rate</th>
                                <th>Date</th>
                                <th>Due Date</th>
                                <th>Days Left</th>
                                <th className="text-end">Payable</th>
                                <th className="text-end">Pending</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bills.map(bill => {
                                const due = getDueStatus(bill.purchaseDate, bill.paymentTermsDays);
                                const pending = (bill.partyPayable || 0) - (bill.paidAmount || 0);
                                const displayWeight = bill.originalWeight || bill.weight;
                                let rowClass = "";
                                if (due.status === 'Overdue') rowClass = "table-danger";
                                else if (due.status === 'Urgent') rowClass = "table-warning";

                                return (
                                    <tr key={bill._id} className={rowClass}>
                                        <td className="fw-bold text-primary">{bill.rpNumber}</td>
                                        <td>{bill.party?.companyName}</td>
                                        <td><span className={`badge ${bill.billType === 'Cash' ? 'bg-success' : 'bg-secondary'}`}>{bill.billType}</span></td>
                                        <td className="text-end fw-bold">{formatDecimal(displayWeight)}</td>
                                        <td className="text-end text-muted">{formatRound(bill.rate)}</td>
                                        <td>{new Date(bill.purchaseDate).toLocaleDateString('en-GB')}</td>
                                        <td>{due.date}</td>
                                        <td>
                                            {due.days !== '-' && (
                                                <span className={`fw-bold ${due.days < 0 ? 'text-danger' : due.days <= 3 ? 'text-warning' : 'text-success'}`}>
                                                    {due.days < 0 ? `${Math.abs(due.days)} Days Late` : `${due.days} Days Left`}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-end">{formatRound(bill.partyPayable)}</td>
                                        <td className="text-end fw-bold">₹ {formatRound(pending)}</td>
                                        <td className="text-center">
                                            <button className="btn btn-sm btn-primary" onClick={() => openPaymentModal(bill)}>Pay</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MOBILE CARDS */}
            <div className="d-block d-md-none">
                {bills.map(bill => {
                    const due = getDueStatus(bill.purchaseDate, bill.paymentTermsDays);
                    const pending = (bill.partyPayable || 0) - (bill.paidAmount || 0);
                    const displayWeight = bill.originalWeight || bill.weight;

                    // Border Color Logic
                    let borderClass = "border-start border-4 ";
                    if (due.status === 'Overdue') borderClass += "border-danger";
                    else if (due.status === 'Urgent') borderClass += "border-warning";
                    else borderClass += "border-success";

                    // Text Color for Due Date
                    let dateClass = "text-muted";
                    if (due.status === 'Overdue') dateClass = "text-danger fw-bold";
                    else if (due.status === 'Urgent') dateClass = "text-warning fw-bold";

                    return (
                        <div key={bill._id} className={`card mb-3 shadow-sm ${borderClass}`}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fw-bold">{bill.party?.companyName}</h6>
                                    <span className="badge bg-light text-dark border">{bill.rpNumber}</span>
                                </div>
                                
                                <div className="d-flex justify-content-between small mb-1">
                                    <span className="text-muted">{formatDecimal(displayWeight)} Cts @ {formatRound(bill.rate)}</span>
                                    <span className="text-muted">Inv: {new Date(bill.purchaseDate).toLocaleDateString('en-GB')}</span>
                                </div>

                                {/* NEW: DUE DATE ROW */}
                                <div className={`d-flex justify-content-between small ${dateClass} mb-2`}>
                                    <span>Due: {due.date}</span>
                                    <span>
                                        {due.days !== '-' ? (
                                            due.days < 0 ? `${Math.abs(due.days)} Days Late` : `${due.days} Days Left`
                                        ) : 'No Term'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                    <div>
                                        <div className="small text-muted" style={{fontSize:'0.7rem'}}>Pending Amount</div>
                                        <h5 className="mb-0 fw-bold text-primary">₹ {formatRound(pending)}</h5>
                                    </div>
                                    <button className="btn btn-primary btn-sm px-4" onClick={() => openPaymentModal(bill)}>Pay</button>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* MODAL */}
            {showModal && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-dark text-white">
                                <h5 className="modal-title">Record Payment</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={submitPayment}>
                                <div className="modal-body">
                                    <div className="alert alert-secondary py-2 mb-3">
                                        <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                                            <strong>{selectedBill?.party?.companyName}</strong>
                                            <span>{new Date(selectedBill?.purchaseDate).toLocaleDateString('en-GB')}</span>
                                        </div>
                                        <div className="row g-2 small">
                                            <div className="col-6">Bill: <strong>{selectedBill?.rpNumber}</strong></div>
                                            <div className="col-6">Bill Weight: <strong>{formatDecimal(selectedBill?.originalWeight || selectedBill?.weight)} Cts</strong></div>
                                            <div className="col-6">Bill Amount: <strong>₹ {formatRound(selectedBill?.partyPayable)}</strong></div>
                                            <div className="col-6 text-danger">Pending: <strong>₹ {formatRound((selectedBill?.partyPayable || 0) - (selectedBill?.paidAmount || 0))}</strong></div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Amount Paying</label>
                                        <input type="number" className="form-control" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Mode</label>
                                        <select className="form-select" value={payForm.mode} onChange={e => setPayForm({...payForm, mode: e.target.value})}>
                                            <option>Cheque</option>
                                            <option>RTGS</option>
                                            <option>Cash</option>
                                            <option>Angadia</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Reference / Cheque No</label>
                                        <input type="text" className="form-control" value={payForm.ref} onChange={e => setPayForm({...payForm, ref: e.target.value})} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-success">Confirm</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountsPayable;